import React, {PropsWithChildren, useContext, useEffect, useMemo} from "react";
import {api} from "../../../api/API";
import {EventEmitter} from "nate-react-api-helpers";
import {CircularProgress, Menu} from "@mui/material";
import {Option} from "../../../misc/scroller/Table";
import {DDLValue} from "../../../api/DDLOptions";
import {useSyncedRef} from "../../../misc/SyncedRef";

export type ValueItem = DDLValue & Option & {
    calculatedPrintLabel: string;
};

export class RootController {
    loader: () => Promise<DDLValue[]>
    loadingP: Promise<ValueItem[]>|null = null;
    data = new EventEmitter<ValueItem[]>();
    loading = new EventEmitter<boolean>();
    _loading = false;
    alwaysReload: boolean = false;

    id: number = 0;
    static idRef = 1;

    constructor(loader: () => Promise<DDLValue[]>) {
        this.loader = loader;
        this.loading.emit(false);
        this.id = RootController.idRef++;
    }

    reload() {
        this.load();
    }

    checkLoading() {
        if (this.loadingP && !this.alwaysReload) return;
        if(this._loading) return; // already loading

        this.load();
    }

    load() {
        this.loading.emit(true);
        this._loading = true;

        this.loadingP = this.loader()
            .then(data => {
                const value = data
                    .filter(o => !o.archived)
                    .map(o => {
                        let display;
                        if(o.label && o.label !== o.value) {
                            display = o.value + ": " + o.label
                        } else {
                            display = o.value;
                        }

                        return {
                            ...o,
                            value: o.value,
                            display: display,
                            calculatedPrintLabel: o.printLabel || o.label || o.value,
                        } as ValueItem;
                    });

                this.data.emit(value);
                return value;
            })
            .finally(() => {
                this._loading = false;
                this.loading.emit(false);
            });
    }
}

export class LookupController {
    data = new EventEmitter<ValueItem[]>();
    parent: RootController;

    constructor(parent: RootController, filter: (v: ValueItem) => boolean) {
        this.parent = parent;

        this.parent.data.subscribeAndFireLast(data => {
            this.data.emit(data.filter(filter));
        });
    }

    checkLoading() {
        this.parent.checkLoading();
    }
}

const root = new RootController(() => api.ddlOptions.list({}));
root.alwaysReload = true;

export const DropDownContext = React.createContext({
    root: root,
    frameProfiles: null as any as LookupController,
    screenElevation: null as any as LookupController,
    doorLiteKit: null as any as LookupController,
    doorElevations: null as any as LookupController,
    doorCores: null as any as LookupController,
    doorSeam: null as any as LookupController,
    doorMaterial: null as any as LookupController,
    doorMaterialWoodSpecies: null as any as LookupController,
    doorMaterialWoodCut: null as any as LookupController,
    doorMaterialWoodMatching: null as any as LookupController,
    doorMaterialWoodMatchingMethod: null as any as LookupController,
    doorMaterialWoodFinish: null as any as LookupController,
    doorMaterialWoodPly: null as any as LookupController,
    loading: true as boolean,
    reload: () => {

    }
});

export function DropDownProvider(props: PropsWithChildren<{}>) {

    const ctx = useDdlDataWithoutContext({
        defaultLoadingYes: true,
    });

    return <DropDownContext.Provider value={ctx}>
        {props.children}
    </DropDownContext.Provider>
}

export function useDropdownData(input: LookupController | RootController) {

    // only check during "mount" to avoid reloading root controller too often when
    // local provider is missing.
    useEffect(() => {
        input.checkLoading()
    }, [input]);

    return (EventEmitter.reactValue(input.data) || []) as ValueItem[];
}

export function DropdownDataLoading(props: {
    anchor: any;
    width: number;
}) {
    return (
        <Menu open anchorEl={props.anchor}>
            <div style={{width: props.width}}>
                <CircularProgress />
            </div>
        </Menu>
    )
}

export function useDropdownDataIMP() {
    const ctx = useContext(DropDownContext);
    const root = ctx.root;

    // only check during "mount" to avoid reloading root controller too often when
    // local provider is missing.
    useEffect(() => {
        root.checkLoading()
    }, [root]);

    return useSyncedRef(() => {
        if(!ctx.root.loadingP) throw new Error("missing loading (invalid state)");
        return ctx.root.loadingP as Promise<ValueItem[]>;
    })
}

export function ddlDoorSeriesFilter(opt, search) {
    if(!opt.doorSeries) return true;
    return opt.doorSeries === search.props("doorSeries").doorSeries;
}

export function useDdlDataWithoutContext(props?: {
    defaultLoadingYes?: boolean;
    project?: number;
}) {
    const defaultLoadingYes = props?.defaultLoadingYes || false;
    const project = props?.project;
    const base = useMemo(() => {
        const root = new RootController(() => api.ddlOptions.list({
            project: project,
        }));

        if(defaultLoadingYes) {
            root.loading.emit(true);
        }

        return {
            root,
            frameProfiles: new LookupController(root, d => d.category === "frameProfile"),
            doorLiteKit: new LookupController(root, d => d.category === "doorLiteKit"),
            doorElevations: new LookupController(root, d => d.category === "doorElevation"),
            doorCores: new LookupController(root, d => d.category === "doorCore"),
            doorSeam: new LookupController(root, d => d.category === "doorSeam"),
            doorMaterial: new LookupController(root, d => d.category === "doorMaterial"),
            doorMaterialWoodCut: new LookupController(root, d => d.category === "doorMaterialWoodCut"),
            doorMaterialWoodSpecies: new LookupController(root, d => d.category === "doorMaterialWoodSpecies"),
            doorMaterialWoodMatching: new LookupController(root, d => d.category === "doorMaterialWoodMatching"),
            doorMaterialWoodMatchingMethod: new LookupController(root, d => d.category === "doorMaterialWoodMatchingMethod"),
            doorMaterialWoodFinish: new LookupController(root, d => d.category === "doorMaterialWoodFinish"),
            doorMaterialWoodPly: new LookupController(root, d => d.category === "doorMaterialWoodPly"),
            screenElevation: new LookupController(root, d => d.category === "screenElevation"),
            reload: () => root.reload(),
        }
    }, [defaultLoadingYes, project]);

    const loading = EventEmitter.reactValue(base.root.loading) || false;

    return useMemo(() => {
        return {
            ...base,
            loading,
        }
    }, [base, loading]);
}